/*
 * Sizes an Acuity scheduling iframe to its content.
 *
 * WHY THIS EXISTS, given Acuity ships embed.js to do exactly this.
 *
 * embed.js finds the frames it manages with a single, one-shot
 * `getElementsByTagName('iframe')` sweep at the moment it executes, and only
 * frames present in the DOM at that instant ever get sized. That is fragile in
 * two ways we actually hit: the classes page builds its iframe in JavaScript
 * after a shopper picks a store, so there is no frame to find when the script
 * runs; and on the store page a schedule taller than 800px kept its own
 * scrollbar rather than growing, which is the bug that started this.
 *
 * The message it is missing is not a secret handshake. Acuity's scheduling app
 * (assets.squarespace.com/scheduling-pylon/main.*.js) runs a ResizeObserver on
 * its own body and posts `sizing:<height>` to `window.parent` every time that
 * height changes — unconditionally, with nothing to opt into and no init call
 * to answer. Listening for it directly is both simpler than their loader and
 * immune to when the frame appeared, because a new message arrives on every
 * resize rather than once at load.
 *
 * embed.js is still loaded alongside this: it also scrolls the page to the
 * frame as a shopper moves through the booking steps, which is worth keeping.
 * Both setting the same height is harmless.
 *
 * The `!important` matches what embed.js writes, so whichever gets there first
 * wins the same way and neither can be undone by a stylesheet.
 */
(function () {
  /* Frames are same-origin with none of these, so the message is the only thing
     crossing the boundary — the origin check is what makes it trustworthy.
     Anchored at both ends: a bare `indexOf` test, which is what embed.js does,
     would accept `acuityscheduling.com.example.net`. */
  var ORIGIN = /^https:\/\/([a-z0-9-]+\.)*(acuityscheduling\.com|acuityscheduling\.net|squarespacescheduling\.com|as\.me)$/;

  /* Matched by contentWindow rather than by src: a page can hold two frames on
     the same origin — a store with both a class calendar and a workshop one —
     and the URL cannot tell us which of them just changed height. */
  function frameFor(source) {
    var frames = document.getElementsByTagName('iframe');
    for (var i = 0; i < frames.length; i++) {
      if (frames[i].contentWindow === source) return frames[i];
    }
    return null;
  }

  window.addEventListener('message', function (event) {
    if (!ORIGIN.test(event.origin)) return;
    if (typeof event.data !== 'string') return;

    var parts = event.data.split(':');
    if (parts[0] !== 'sizing') return;

    /* The same floor embed.js uses. A schedule that briefly reports a couple of
       hundred pixels while it boots would otherwise collapse the frame to a
       sliver and then snap it back. */
    var height = parseInt(parts[1], 10);
    if (!(height > 150)) return;

    var frame = frameFor(event.source);
    if (!frame) return;

    frame.style.setProperty('height', height + 'px', 'important');
    frame.style.setProperty('max-height', 'none');
    frame.style.setProperty('overflow', 'hidden');

    /* The scrollbar does not go away on height alone.
     *
     * `overflow: hidden` on an IFRAME ELEMENT is not what governs the scrollbar
     * inside it — that belongs to the embedded document's own viewport, and
     * WebKit ignores the outer rule. embed.js sets it too and it does nothing
     * there either. The frame measured 6119px, exactly its content, and Safari
     * still drew a bar: the schedule reports `document.body.scrollHeight`,
     * which excludes body margin, so the document is always a few pixels
     * taller than the height it asks for.
     *
     * `scrolling="no"` is the thing that actually governs it. Set HERE rather
     * than in the markup, and only once a real height has arrived, because in
     * the markup it would also apply to a frame that never got sized — leaving
     * a shopper with a schedule clipped at 800px and no way to reach the rest
     * of it. Sized first, then locked. A browser that ignores the attribute
     * change is no worse off than before it. */
    frame.setAttribute('scrolling', 'no');
  });
}());
